import { useState, useEffect, useMemo, useRef } from 'react'
import { cn } from '@utils'
import { InputType, StepButtonType, QuantityType } from '@/constants'
import { useSessionStore } from '@/stores'
import { useUIStore } from '@/stores'
import { apiClient } from '@/services/api'
import { formatCurrency } from '@/utils/helpers'
import { getCustomInputClass, parseStyleProp } from '@/utils/dom'
import { updateFontSizes } from '@/utils/fontSizing'
import SessionContent from './SessionContent'
import StepBackButton from './StepBackButton'
import ExchangeProduct from './ExchangeProduct'

interface SelectExchangeProductProps {
  item?: any
  subscriptionOnly?: boolean
  quantityType?: number
  currentSession?: any
  onSelect?: (input: any, data?: any, campaignProduct?: any) => void
  onBack?: () => void
}

const SelectExchangeProduct = ({
  item,
  subscriptionOnly = false,
  quantityType = QuantityType.Any,
  currentSession,
  onSelect,
  onBack,
}: SelectExchangeProductProps) => {
  const session = useSessionStore(state => state.session)
  const popupSession = useSessionStore(state => state.popupSession)
  const setLoading = useUIStore(state => state.setLoading)
  const setCriticalError = useUIStore(state => state.setCriticalError)
  const fetchedItemIdRef = useRef<number | null>(null)

  const [originalProduct, setOriginalProduct] = useState<any>(null)
  const [selectedExchangeableProduct, setSelectedExchangeableProduct] = useState<any>(null)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [selectProductFields, setSelectProductFields] = useState<Record<string, string>>({})
  const [minQuantity, setMinQuantity] = useState(1)
  const [maxQuantity, setMaxQuantity] = useState(10)
  const [exchangeableVariants, setExchangeableVariants] = useState<any[]>([])

  const activeSession = useMemo(() => {
    if (currentSession) {
      return currentSession
    }
    if (session?.is_popup && popupSession) {
      return popupSession
    }
    return session
  }, [currentSession, session, popupSession])

  const selectedProduct = useMemo(
    () => selectedExchangeableProduct?.to_campaign_product || null,
    [selectedExchangeableProduct]
  )

  const selectedQuantity = useMemo(() => selectedExchangeableProduct?.quantity || 1, [selectedExchangeableProduct])

  const selectedPrice = useMemo(() => Number(selectedExchangeableProduct?.price || 0), [selectedExchangeableProduct])

  const selectedVariant = useMemo(() => selectedExchangeableProduct?.to_variant || null, [selectedExchangeableProduct])

  const exchangeablesAreVariants = useMemo(() => exchangeableVariants?.length > 0, [exchangeableVariants])

  const exchangeableProducts = useMemo(() => {
    if (exchangeablesAreVariants) {
      return exchangeableVariants
    }
    if (!selectedItem) {
      return []
    }

    const selectedVariantId = selectedItem.variant?.id || null
    return (selectedItem.campaign_product?.exchangeable_products || []).filter(
      (ep: any) =>
        ep.to_campaign_product.id !== selectedItem.campaign_product.id ||
        ep.quantity !== selectedItem.quantity ||
        (ep.to_variant?.id || null) !== selectedVariantId ||
        Number(ep.price) !== Number(selectedItem.price)
    )
  }, [exchangeableVariants, exchangeablesAreVariants, selectedItem])

  const isSelected = (campaignProduct: any, quantity: number, price: any, variant: any): boolean => {
    const variantId = variant?.id || null
    const selVariantId = selectedVariant?.id || null

    if (exchangeablesAreVariants) {
      return selVariantId === variantId
    }

    return !!(
      selectedProduct &&
      selectedProduct.id === campaignProduct?.id &&
      selectedQuantity === quantity &&
      selVariantId === variantId &&
      !isNaN(Number(price)) &&
      selectedPrice === Number(price)
    )
  }

  const totalPrice = useMemo(() => {
    if (!selectedItem) {
      return ''
    }
    if (
      isSelected(selectedItem.campaign_product, selectedItem.quantity, selectedItem.unit_price, selectedItem.variant)
    ) {
      return formatCurrency(selectedItem.current_price, selectedItem.order?.currency)
    }
    const unitPrice = selectedExchangeableProduct?.price || 0
    return formatCurrency(Number(unitPrice) * selectedQuantity, selectedItem.order?.currency)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isSelected is re-created each render.
  }, [selectedExchangeableProduct, selectedItem, selectedQuantity, selectedProduct, selectedVariant, selectedPrice])

  const needsProductSelection = (input: any): boolean => {
    if (input.id in selectProductFields) {
      if (selectedProduct) {
        return isSelected(
          selectedItem?.campaign_product,
          selectedItem?.quantity,
          selectedItem?.unit_price,
          selectedItem?.variant
        )
      }
      return true
    }
    return false
  }

  const isLink = (input: any): boolean =>
    input.type === InputType.Link || input.button_styles?.style_type === StepButtonType.TextOnly

  const sessionInputLabel = (input: any): string => {
    if (input) {
      return updateFontSizes(input.formatted_label)
    }
    return ''
  }

  const handleSelectInput = (input: any) => {
    if (!needsProductSelection(input)) {
      let inputData: Record<string, any> | null = null
      let campaignProduct: any = null

      if (selectedProduct && input.id in selectProductFields) {
        inputData = {}
        campaignProduct = selectedProduct
        inputData[selectProductFields[input.id]] = selectedProduct.id

        if (selectedQuantity) {
          inputData['quantity'] = selectedQuantity
        }

        if (selectedVariant) {
          inputData['variants'] = [{ id: selectedVariant.id, quantity: selectedQuantity }]
          inputData['variant_is_fixed'] = true
        }
      }

      onSelect?.(input, inputData, campaignProduct)
    }
  }

  const updateQuantity = (val: string) => {
    const quantity = parseInt(val)
    if (quantity > 0 && quantity !== selectedQuantity && selectedExchangeableProduct) {
      if (originalProduct && originalProduct === selectedExchangeableProduct) {
        setOriginalProduct({ ...originalProduct, quantity })
      }
      setSelectedExchangeableProduct({ ...selectedExchangeableProduct, quantity })
    }
  }

  useEffect(() => {
    if (!activeSession?.id) {
      return
    }

    const currentItem = item || activeSession.selected_item
    if (!currentItem) {
      return
    }

    if (fetchedItemIdRef.current === currentItem.id) {
      return
    }
    fetchedItemIdRef.current = currentItem.id

    setLoading(true)

    const loadExchangeables = async () => {
      try {
        const [cpResult, exchangeResult] = await Promise.all([
          apiClient.get(`/campaign_products/${currentItem.campaign_product.id}/`),
          apiClient.get(`/order_items/${currentItem.id}/exchangeables/`, {
            params: {
              subscription_only: subscriptionOnly,
              quantity_type: quantityType,
            },
          }),
        ])

        const campaignProduct = cpResult.data
        const exchangeableProductsData: any[] = exchangeResult.data?.exchangeables || []
        const variantsData: any[] = exchangeResult.data?.variants || []

        campaignProduct.exchangeable_products = exchangeableProductsData

        let unitPrice = currentItem.unit_price
        let variant = currentItem.variant
        const quantity = currentItem.quantity

        if (subscriptionOnly) {
          unitPrice = currentItem.unit_recurring_price
          if (currentItem.next_cycle_product && currentItem.next_cycle_product.id !== currentItem.product.id) {
            campaignProduct.product = currentItem.next_cycle_product
            campaignProduct.name = null
            campaignProduct.image = null
          }
          if (currentItem.next_variant) {
            variant = currentItem.next_variant
          }
        }

        const updatedItem = {
          ...currentItem,
          campaign_product: campaignProduct,
        }

        let originalProd: any = {
          to_campaign_product: campaignProduct,
          quantity,
          price: unitPrice,
          display_name: null,
          sales_phrase: currentItem.product?.sales_phrase,
          subtext: currentItem.subtext,
          retail_price: currentItem.retail_price,
          to_variant: variant,
        }

        if (exchangeableProductsData?.length) {
          exchangeableProductsData.some((ep: any) => {
            if (ep.to_campaign_product.id === currentItem.campaign_product.id && ep.quantity === currentItem.quantity) {
              originalProd = ep
              return true
            }
            return false
          })
        } else if (variantsData?.length) {
          const newVariants = variantsData.map((v: any) => ({
            to_campaign_product: campaignProduct,
            quantity: (v.quantity || 1) * currentItem.quantity,
            price: v.price || currentItem.unit_price,
            display_name: null,
            sales_phrase: null,
            subtext: currentItem.subtext,
            retail_price: v.retail_price || currentItem.retail_price,
            to_variant: v,
          }))
          setExchangeableVariants(newVariants)
        }

        const newSelectProductFields: Record<string, string> = {}
        let newMin = 1
        let newMax = 10

        for (const input of activeSession.valid_inputs || []) {
          const keys = Object.keys(input.input_fields || {})
          keys.forEach((key: string) => {
            const config = input.input_fields[key]
            if (config.type === InputType.Product) {
              newSelectProductFields[input.id] = key
            } else if (key === 'quantity') {
              if (config.min) {
                newMin = config.min
              }
              if (config.max) {
                newMax = config.max
              }
            }
          })
        }

        setSelectedItem(updatedItem)
        setOriginalProduct(originalProd)
        setSelectedExchangeableProduct(originalProd)
        setSelectProductFields(newSelectProductFields)
        setMinQuantity(newMin)
        setMaxQuantity(newMax)
      } catch (err) {
        console.error(err)
        setCriticalError(err)
      } finally {
        setLoading(false)
      }
    }

    loadExchangeables()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Keyed on the session id; the nested session fields change on every poll.
  }, [activeSession?.id, item, subscriptionOnly, quantityType])

  const isReady = !!(selectedItem && originalProduct)

  return (
    <div className="exchange-section px-3 sm:px-4">
      <SessionContent currentSession={activeSession} session={activeSession} />

      {isReady && (
        <>
          <h6 className="section-heading">Your Current {subscriptionOnly ? 'Subscription' : 'Order'}</h6>
          <ExchangeProduct
            product={originalProduct}
            selected={isSelected(
              originalProduct.to_campaign_product,
              originalProduct.quantity,
              originalProduct.price,
              originalProduct.to_variant
            )}
            original
            item={selectedItem}
            subscriptionOnly={subscriptionOnly}
            onSelect={setSelectedExchangeableProduct}
          />

          {exchangeableProducts.length > 0 ? (
            <div className="mt-6">
              <h6 className="section-heading">{subscriptionOnly ? 'New Subscription' : 'Other Package'} Options</h6>
              <p className="section-subheading">
                *Replace your current {subscriptionOnly ? 'subscription' : 'order'} with a new{' '}
                {subscriptionOnly ? 'product' : 'package'} below:
              </p>
              {exchangeableProducts.map((ep: any, idx: number) => (
                <ExchangeProduct
                  key={ep.id || `ep-${idx}`}
                  product={ep}
                  selected={isSelected(ep.to_campaign_product, ep.quantity, ep.price, ep.to_variant)}
                  original={false}
                  item={selectedItem}
                  subscriptionOnly={subscriptionOnly}
                  onSelect={setSelectedExchangeableProduct}
                />
              ))}
            </div>
          ) : (
            <div className="mt-4">
              <label htmlFor="quantity" className="block text-base font-medium mb-1">
                Quantity
              </label>
              <select
                id="quantity"
                className="form-select"
                value={selectedQuantity}
                onChange={e => updateQuantity(e.target.value)}>
                {Array.from({ length: maxQuantity - minQuantity + 1 }, (_, i) => i + minQuantity).map(qty => (
                  <option key={qty} value={qty}>
                    {qty}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="total-row notranslate">
            <span className="label">Total:</span>
            <span className="amount">{totalPrice}</span>
          </div>

          {!subscriptionOnly && <p className="refund-note">*We will issue a refund of the difference you paid.</p>}

          <div className="flex flex-col items-center gap-3 mt-4 mb-2">
            {(activeSession?.valid_inputs || []).map((sessionInput: any) => {
              const isDisabled = needsProductSelection(sessionInput)
              return isLink(sessionInput) ? (
                <button
                  key={`choice-${sessionInput.id}`}
                  type="button"
                  disabled={isDisabled}
                  className={cn('input-link', getCustomInputClass(activeSession, sessionInput))}
                  style={parseStyleProp(sessionInput.button_styles?.style)}
                  dangerouslySetInnerHTML={{ __html: sessionInputLabel(sessionInput) }}
                  onClick={() => handleSelectInput(sessionInput)}
                />
              ) : (
                <button
                  key={`choice-${sessionInput.id}`}
                  type="button"
                  disabled={isDisabled}
                  className={cn('btn-base input-choice-btn', getCustomInputClass(activeSession, sessionInput, true))}
                  style={parseStyleProp(sessionInput.button_styles?.style)}
                  onClick={() => handleSelectInput(sessionInput)}>
                  <div dangerouslySetInnerHTML={{ __html: sessionInputLabel(sessionInput) }} />
                </button>
              )
            })}
          </div>
        </>
      )}

      {activeSession && <StepBackButton onBack={onBack} />}
    </div>
  )
}

export default SelectExchangeProduct
